<template>
  <div class="datetime-range-picker w-full">
    <div class="flex space-x-3 w-full">
      <div class="field w-full sm:w-2/5">
        <label :for="`${startName}`">{{ t('dictionary.start') }}</label>

        <DatePicker
          :id="`${startName}`"
          appendTo="body"
          v-model="startDateTime"
          :stepMinute="15"
          showTime
          hourFormat="12"
          fluid
          :placeholder="placeholder || 'Select start time'"
          :class="{ 'p-invalid': errors.start }"
        />

        <small class="p-error whitespace-nowrap text-red-600">{{ errors.start || '' }}</small>

        <input type="hidden" :name="`${startName}`" :value="formattedStartDateTime || ''" />
      </div>

      <div class="field w-full sm:w-2/5">
        <label :for="`${endName}`">{{ t('dictionary.end') }}</label>

        <DatePicker
          :id="`${endName}`"
          appendTo="body"
          v-model="endDateTime"
          :stepMinute="15"
          showTime
          hourFormat="12"
          fluid
          :placeholder="placeholder || 'Select end time'"
          :minDate="minEndDate"
          :class="{ 'p-invalid': errors.end }"
        />

        <small class="p-error whitespace-nowrap text-red-600">{{ errors.end || '' }}</small>

        <input type="hidden" :name="`${endName}`" :value="formattedEndDateTime || ''" />
      </div>

      <div class="duration-display w-1/5 hidden sm:block">
        <div class="title text-sm">{{ t('dictionary.duration') }}</div>
        <div class="py-1.5 text-sm">{{ durationText || '0 min' }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue'
  import { DateTime, Duration } from 'luxon'
  import DatePicker from 'primevue/datepicker'

  import { useI18n } from 'vue-i18n'

  const DEFAULT_DURATION_MINUTES = 30
  const MIN_DURATION_MINUTES = 15

  const props = defineProps({
    startName:          { type: String, required: true },
    endName:            { type: String, required: true },
    skipDateValidation: { type: Boolean, default: false }, // When user edits a past time, we don't need to call validateBothDates()
    modelValue:         { type: Object, default: null },
    placeholder:        { type: String, default: '' },
    minDuration:        { type: Number, default: MIN_DURATION_MINUTES },
    locale:             { type: String, default: 'en'}
  })

  const { t, locale } = useI18n()

  locale.value = props.locale

  const emit = defineEmits(['update:modelValue', 'validation-error'])

  const startDateTime = ref(null)
  const endDateTime = ref(null)
  const errors = ref({ start: null, end: null })

  onMounted(() => {
    const { start, end } = props.modelValue || {}
    startDateTime.value = start ? new Date(start) : null
    endDateTime.value = end ? new Date(end) : null
  })

  // Must send unmarked datetime (no timezone) so server can interpret as local time.
  // .toISOString() adds 'Z' (UTC marker) causing server to skip timezone conversion, resulting in incorrect times.
  // Format without timezone so convert_params_to_time_zone can properly convert local time → UTC for storage.
  const formattedStartDateTime = computed(() => {
    if (!startDateTime.value) return ''
    const d = startDateTime.value
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    const seconds = String(d.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  })

  const formattedEndDateTime = computed(() => {
    if (!endDateTime.value) return ''
    const d = endDateTime.value
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    const seconds = String(d.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  })

  const minEndDate = computed(() => {
    if (!startDateTime.value) return null
    return DateTime.fromJSDate(startDateTime.value).plus({ minutes: props.minDuration }).toJSDate()
  })

  const durationText = computed(() => {
    if (!startDateTime.value || !endDateTime.value) return null

    const duration = DateTime.fromJSDate(endDateTime.value)
      .diff(DateTime.fromJSDate(startDateTime.value))
      .shiftTo('days', 'hours', 'minutes')

    if (duration.as('minutes') === 0) return '0 min'

    const days = Math.floor(duration.days)
    const hours = Math.floor(duration.hours)
    const minutes = Math.round(duration.minutes)

    // Build string with only non-zero values
    // Example: 1416 hr 0 min -> 59 days
    const parts = []
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`)
    if (hours > 0) parts.push(`${hours} hr`)
    if (minutes > 0) parts.push(`${minutes} min`)

    return parts.join(' ')
  })

  function validateStartDate(date) {
    if (!date) return []
    const now = DateTime.now()
    const startDate = DateTime.fromJSDate(date)
    return startDate < now ? [t('datetime.errors.startPast')] : []
  }

  function validateEndDate(date) {
    const start = startDateTime.value
    if (!date || !start) return []

    const startDate = DateTime.fromJSDate(start)
    const endDate = DateTime.fromJSDate(date)
    const duration = endDate.diff(startDate, 'minutes').minutes

    if (endDate < startDate) return [t('datetime.errors.endBeforeStart')]
    if (duration === 0 || (duration > 0 && duration < props.minDuration)) {
      return [t('datetime.errors.durationTooShort', { min: props.minDuration })]
    }

    return []
  }

  function validateBothDates() {
    const startErrors = validateStartDate(startDateTime.value)
    const endErrors = validateEndDate(endDateTime.value)

    errors.value = {
      start: startErrors[0] || null,
      end: endErrors[0] || null
    }

    const hasErrors = startErrors.length || endErrors.length
    if (hasErrors) {
      emit('validation-error', { start: startErrors, end: endErrors })
    }

    return !hasErrors
  }

  // When startDateTime changes, auto-update end to maintain default 30 min duration.
  // This provides a helpful starting point, but users can adjust end time freely for custom durations.
  watch(startDateTime, (newStart) => {
    if (!newStart) return

    const startDate = DateTime.fromJSDate(newStart)
    endDateTime.value = startDate.plus({ minutes: DEFAULT_DURATION_MINUTES }).toJSDate()
  })

  // No watcher for endDateTime - users can freely adjust the end time for custom durations.
  // The start time remains anchored and won't shift when the user modifies the end time.

  watch([startDateTime, endDateTime], () => {
    if (!props.skipDateValidation) {
      validateBothDates()
    }

    emit('update:modelValue', {
      start: startDateTime.value,
      end: endDateTime.value
    })
  }, { deep: true })

  // This watcher syncs the component's internal state (startDateTime and endDateTime)
  // with external changes to props.modelValue
  // 1. Internal change → emit to parent
  // 2. Parent prop change → watcher updates internal state
  // 3. Internal change → emit to parent
  watch(() => props.modelValue, (newVal) => {
    if (!newVal) {
      startDateTime.value = endDateTime.value = null
      return
    }

    const { start, end } = newVal

    const startDate = start ? new Date(start) : null
    const endDate = end ? new Date(end) : null

    const startChanged = startDate?.getTime() !== startDateTime.value?.getTime()
    const endChanged = endDate?.getTime() !== endDateTime.value?.getTime()

    if (startChanged || endChanged) {
      startDateTime.value = startDate
      endDateTime.value = endDate
    }
  }, { deep: true })
</script>